/**
 * Base Layout Component
 * Provides the foundation layout structure that can be customized per portal
 */

import React from 'react';
import { Box } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useLayout } from '../LayoutProvider';
import Header from '../Header';
import Sidebar from '../Sidebar';
import Footer from '../Footer';
import Breadcrumbs from '../Breadcrumbs';
import { SuperAdminReturnButton } from '../../components/navigation';
import ErrorBoundary from '../../components/ui/ErrorBoundary/ErrorBoundary';
import '../styles/animations.css';

/**
 * Base Layout Component
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components (overrides Outlet)
 * @param {Object} props.headerProps - Props for Header component
 * @param {Object} props.sidebarProps - Props for Sidebar component
 * @param {Object} props.footerProps - Props for Footer component
 * @param {Object} props.breadcrumbProps - Props for Breadcrumbs component
 * @param {boolean} props.showHeader - Override header visibility
 * @param {boolean} props.showSidebar - Override sidebar visibility
 * @param {boolean} props.showFooter - Override footer visibility
 * @param {boolean} props.showBreadcrumbs - Override breadcrumbs visibility
 */
const BaseLayout = ({
  children,
  headerProps = {},
  sidebarProps = {},
  footerProps = {},
  breadcrumbProps = {},
  showHeader: overrideShowHeader,
  showSidebar: overrideShowSidebar,
  showFooter: overrideShowFooter,
  showBreadcrumbs: overrideShowBreadcrumbs,
}) => {
  const { layoutConfig, layoutStyles, sidebarOpen, toggleSidebar, closeSidebar } = useLayout();

  // Determine what to show based on config and overrides
  const showHeader =
    overrideShowHeader !== undefined ? overrideShowHeader : layoutConfig.showHeader;
  const showSidebar =
    overrideShowSidebar !== undefined ? overrideShowSidebar : layoutConfig.showSidebar;
  const showFooter =
    overrideShowFooter !== undefined ? overrideShowFooter : layoutConfig.showFooter;
  const showBreadcrumbs =
    overrideShowBreadcrumbs !== undefined ? overrideShowBreadcrumbs : layoutConfig.showBreadcrumbs;

  return (
    <Box
      sx={{
        ...layoutStyles.container,
        ...layoutStyles.background,
      }}
      className={layoutConfig.backgroundGradient ? 'animate-gradient' : ''}
    >
      {/* Header */}
      {showHeader && (
        <Header
          onMenuClick={toggleSidebar}
          hideMenuButton={!showSidebar}
          {...headerProps}
        />
      )}

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        {showSidebar && (
          <Sidebar
            permanent={!layoutConfig.sidebarFloating}
            onClose={closeSidebar}
            {...sidebarProps}
          />
        )}

        {/* Content Area */}
        <Box
          component="main"
          sx={{
            ...layoutStyles.main,
            marginLeft:
              showSidebar && !layoutConfig.sidebarFloating && !sidebarOpen
                ? `${layoutConfig.sidebarCollapsedWidth}px`
                : 0,
            transition: (theme) =>
              theme.transitions.create(['margin'], {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
          }}
        >
          {/* Breadcrumbs */}
          {showBreadcrumbs && <Breadcrumbs {...breadcrumbProps} />}

          {/* Page Content */}
          <Box
            sx={{
              flexGrow: 1,
              overflow: 'auto',
              position: 'relative',
              zIndex: 1,
            }}
            className="animate-fade-in"
          >
            <ErrorBoundary variant="inline" title="This section encountered an error">
              {children || <Outlet />}
            </ErrorBoundary>

            {/* Super Admin Return Button - shows when user switched from super admin */}
            <SuperAdminReturnButton />
          </Box>

          {/* Footer */}
          {showFooter && <Footer {...footerProps} />}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Layout with Custom Content Area
 * Provides more control over the content area structure
 */
export const CustomContentLayout = ({
  children,
  headerProps = {},
  sidebarProps = {},
  showHeader = true,
  showSidebar = true,
}) => {
  const { layoutConfig, layoutStyles, toggleSidebar, closeSidebar } = useLayout();

  return (
    <Box sx={layoutStyles.container}>
      {/* Header */}
      {showHeader && (
        <Header
          onMenuClick={toggleSidebar}
          hideMenuButton={!showSidebar}
          {...headerProps}
        />
      )}

      {/* Main Content Area */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        {showSidebar && (
          <Sidebar
            permanent={!layoutConfig.sidebarFloating}
            onClose={closeSidebar}
            {...sidebarProps}
          />
        )}

        {/* Custom Content Area */}
        {children}
      </Box>
    </Box>
  );
};

/**
 * Minimal Layout
 * Simple layout with just header and content
 */
export const MinimalLayout = ({ children, headerProps = {}, showHeader = true }) => {
  const { layoutStyles } = useLayout();

  return (
    <Box sx={layoutStyles.container}>
      {/* Header */}
      {showHeader && <Header hideMenuButton={true} {...headerProps} />}

      {/* Content */}
      <Box
        component="main"
        sx={{
          ...layoutStyles.main,
          marginLeft: 0,
        }}
      >
        {children || <Outlet />}
      </Box>
    </Box>
  );
};

export default BaseLayout;
